import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ServiceSolution, ServiceType, ServiceSubscriptionFormData, SERVICE_TO_SOLUTIONS, GAMType } from '../../types/service';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { PercentageSlider } from './PercentageSlider';
import { Label } from '../ui/label';

export const getIdentifierFieldForSolution = (solution?: ServiceSolution | '' | null): keyof ServiceSubscriptionFormData | null => {
  switch (solution) {
    case ServiceSolution.ADBLOCK_WORKAROUND: return 'websiteName';
    case ServiceSolution.NO_CONSENT_OUTBRAIN: return 'outbrainSection';
    case ServiceSolution.SSP: return 'siteKey';
    default: return null;
  }
};

interface ServiceFormFieldsProps {
  data: ServiceSubscriptionFormData;
  onChange: (patch: Partial<ServiceSubscriptionFormData>) => void;
  lockService?: boolean;
  lockSolution?: boolean;
  allowedSolutionsOverride?: ServiceSolution[]; // optional restriction list
  showStatus?: boolean; // handled outside currently
  mode: 'create' | 'edit' | 'quickadd';
  identifierVisibility?: 'editable' | 'readonly' | 'hidden';
}

export const ServiceFormFields: React.FC<ServiceFormFieldsProps> = ({
  data,
  onChange,
  lockService = false,
  lockSolution = false,
  allowedSolutionsOverride,
  mode,
  identifierVisibility = 'editable'
}) => {
  const { t } = useTranslation();

  const solutionOptions = useMemo(() => {
    if (!data.service) return [] as ServiceSolution[];
    let list = SERVICE_TO_SOLUTIONS[data.service as ServiceType];
    if (allowedSolutionsOverride) list = list.filter(s => allowedSolutionsOverride.includes(s));
    return list;
  }, [data.service, allowedSolutionsOverride]);

  const identifierField = getIdentifierFieldForSolution(data.solution as ServiceSolution);
  const identifierLabel = identifierField === 'websiteName' ? t('serviceEdit.websiteName','Website Name')
    : identifierField === 'outbrainSection' ? t('serviceEdit.outbrainSection','Outbrain Section')
    : identifierField === 'siteKey' ? t('serviceEdit.siteKey','Site Key')
    : null;

  const showLegacyCommission = data.solution === ServiceSolution.ADBLOCK_LEGACY;
  const showPercentageCommission = !showLegacyCommission && !!data.solution;
  const showGamFields = data.solution === ServiceSolution.ADBLOCK_WORKAROUND || data.solution === ServiceSolution.NO_CONSENT_GAM;
  const showScriptVariant = data.solution === ServiceSolution.ADBLOCK_WORKAROUND;
  const showSspFields = data.solution === ServiceSolution.SSP;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>{t('serviceEdit.serviceType','Type de service')} *</Label>
          <Select
            value={data.service || ''}
            onValueChange={(v) => onChange({ service: v as ServiceType, solution: '' as any })}
            disabled={lockService || mode === 'edit'}
          >
            <SelectTrigger><SelectValue placeholder={t('serviceEdit.selectServiceType','Sélectionner')} /></SelectTrigger>
            <SelectContent>
              {Object.values(ServiceType).map(st => <SelectItem key={st} value={st}>{st}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>{t('serviceEdit.solution','Solution')} *</Label>
          <Select
            value={data.solution || ''}
            onValueChange={(v) => onChange({ solution: v as ServiceSolution })}
            disabled={!data.service || lockSolution || (mode === 'edit')}
          >
            <SelectTrigger><SelectValue placeholder={t('serviceEdit.selectSolutionType','Sélectionner')} /></SelectTrigger>
            <SelectContent>
              {solutionOptions.map(sol => <SelectItem key={sol} value={sol}>{sol}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {identifierField && identifierVisibility !== 'hidden' && (
        <div className="space-y-1">
          <Label>{identifierLabel} *</Label>
          <Input
            value={(data as any)[identifierField] || ''}
            onChange={(e) => onChange({ [identifierField]: e.target.value } as any)}
            placeholder={identifierLabel || ''}
            disabled={identifierVisibility === 'readonly'}
          />
        </div>
      )}

      {showLegacyCommission && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>{t('serviceEdit.commPublisher','Commission Publisher %')}</Label>
            <Input type="number" min={0} max={100} step={0.1}
                   value={data.commissionPublisher ?? ''}
                   onChange={e=>onChange({ commissionPublisher: e.target.value ? parseFloat(e.target.value) : null })} />
          </div>
          <div className="space-y-1">
            <Label>{t('serviceEdit.commDblock','Commission D-Block %')}</Label>
            <Input type="number" min={0} max={100} step={0.1}
                   value={data.commissionDblock ?? ''}
                   onChange={e=>onChange({ commissionDblock: e.target.value ? parseFloat(e.target.value) : null })} />
          </div>
        </div>
      )}

      {showPercentageCommission && (
        <PercentageSlider
          value={data.commissionPercentage ?? null}
            onChange={(val) => onChange({ commissionPercentage: val })}
          label={t('serviceEdit.commissionPercentage','Commission %')}
          step={0.1}
        />
      )}

      {showGamFields && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>{t('serviceEdit.gamType','GAM Type')}</Label>
            <Select value={data.gamType || ''} onValueChange={(v)=>onChange({ gamType: v as GAMType })}>
              <SelectTrigger><SelectValue placeholder={t('common.select','Sélectionner')} /></SelectTrigger>
              <SelectContent>
                {Object.values(GAMType).map(gt => <SelectItem key={gt} value={gt}>{gt}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>{t('serviceEdit.gamAccountId','GAM Account ID')}</Label>
            <Input value={data.gamAccountId || ''} onChange={e=>onChange({ gamAccountId: e.target.value })} />
          </div>
        </div>
      )}

      {showScriptVariant && (
        <div className="space-y-1">
          <Label>{t('serviceEdit.scriptVariant','Script Variant')}</Label>
          <Input value={data.scriptVariant || ''} onChange={e=>onChange({ scriptVariant: e.target.value })} />
        </div>
      )}

      {showSspFields && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>{t('serviceEdit.integrationType','Integration Type')}</Label>
            <Input value={data.integrationType || ''} onChange={e=>onChange({ integrationType: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>{t('serviceEdit.protocol','Protocol')}</Label>
            <Input value={data.protocol || ''} onChange={e=>onChange({ protocol: e.target.value })} />
          </div>
        </div>
      )}
    </div>
  );
};
